import type { EldrickPayload } from '../equipment/types';
import { importedLaunch, parseImportedFitting } from '../testing/ImportedFitting';

/** Core is the fitting product; Lab is its visual, interactive continuation. */
export class FittingArrival {
  private root = document.createElement('section');
  private controller: AbortController | null = null;

  constructor(private onStart: (payload: EldrickPayload) => void) {
    this.root.id = 'fitting-arrival';
    this.root.setAttribute('aria-label', 'Your Eldrick fitting');
    this.root.innerHTML = `
      <div class="arrival-brand">ELDRICK <span>LAB</span></div>
      <div class="arrival-card">
        <div id="arrival-source" class="arrival-eyebrow">FROM ELDRICK CORE</div>
        <h1 id="arrival-title" tabindex="-1">See your fitting.<br><em>In full flight.</em></h1>
        <p id="arrival-status" role="status" aria-live="polite">Loading your swing and recommended setup…</p>
        <div id="arrival-content" hidden>
          <div class="arrival-metrics">
            <div><span>Ball speed</span><strong id="arrival-speed"></strong><small>mph</small></div>
            <div><span>Launch</span><strong id="arrival-launch"></strong><small>degrees</small></div>
            <div><span>Spin</span><strong id="arrival-spin"></strong><small>rpm</small></div>
          </div>
          <div class="arrival-setup"><span>YOUR CURRENT SETUP</span><strong id="arrival-setup"></strong></div>
          <details class="arrival-evidence"><summary>How we modeled it</summary>
            <p id="arrival-provenance"></p>
            <p>Ball flight and equipment differences are simulated estimates—not guaranteed gains.</p>
            <p>Unmapped specs use neutral defaults. Lie, adapter and strike adaptation are not modeled.</p>
            <a href="/validation.html" target="_blank" rel="noopener">Evidence & limitations ↗</a>
          </details>
        </div>
        <button id="arrival-action" type="button" disabled>LOADING FITTING…</button>
        <a id="arrival-core" href="https://eldrick.com/my-fittings?from=lab" hidden>OPEN ELDRICK CORE →</a>
        <a id="arrival-exit" href="/">Explore a sample swing</a>
      </div>
      <div class="arrival-range-label" aria-hidden="true">THE ELDRICK RANGE <span>01 / YOUR FITTING</span></div>`;
    document.body.append(this.root);
    const app = document.getElementById('mobile-root')!;
    app.inert = true;
    app.style.visibility = 'hidden';
    this.root.setAttribute('aria-busy', 'true');
  }

  private el<T extends HTMLElement = HTMLElement>(id: string): T {
    return this.root.querySelector<T>(`#arrival-${id}`)!;
  }

  async load(sessionId: string): Promise<void> {
    const fromApi = sessionId.startsWith('api_');
    this.controller?.abort();
    const controller = new AbortController();
    this.controller = controller;
    const timeout = window.setTimeout(() => controller.abort(), 12000);
    const action = this.el<HTMLButtonElement>('action');
    action.disabled = true;
    action.textContent = 'LOADING FITTING…';
    this.el<HTMLAnchorElement>('core').hidden = true;
    this.el('status').textContent = 'Loading your swing and recommended setup…';
    this.root.setAttribute('aria-busy', 'true');
    try {
      const response = await fetch(`/api/session/${encodeURIComponent(sessionId)}`, { signal: controller.signal, cache: 'no-store' });
      if (!response.ok) throw new Error(response.status === 422 ? 'incomplete' : response.status === 404 || response.status === 410 ? 'expired' : 'unavailable');
      this.present(parseImportedFitting(await response.json()));
    } catch (error) {
      const reason = error instanceof Error ? error.message : '';
      this.el('status').textContent = fromApi
        ? reason === 'expired' || reason === 'invalid_fitting'
          ? 'This API fitting link is unavailable. Return to the app that created it and open a new Lab link.'
          : 'We couldn’t reach this API fitting. Your link is still here—try connecting again.'
        : reason === 'incomplete'
        ? 'This fitting is not complete yet. Finish it in Eldrick Core, then Core will bring you back here with your swing and specs.'
        : reason === 'expired'
        ? 'This fitting link is unavailable or has expired. Reopen your fitting from Eldrick Core, or try again.'
        : reason === 'invalid_fitting'
          ? 'This fitting has incomplete or unsupported data. Reopen it from Eldrick Core; we haven’t substituted a sample fitting.'
          : 'We couldn’t reach your fitting. Your link is still here—try connecting again.';
      action.textContent = 'TRY AGAIN';
      action.disabled = false;
      action.onclick = () => { void this.load(sessionId); };
      const core = this.el<HTMLAnchorElement>('core');
      core.href = fromApi
        ? '/'
        : reason === 'incomplete'
        ? `https://eldrick.com/results/${encodeURIComponent(sessionId)}?from=lab`
        : 'https://eldrick.com/my-fittings?from=lab';
      core.textContent = fromApi
        ? 'RETURN TO ELDRICK LAB →'
        : reason === 'incomplete' ? 'FINISH IN ELDRICK CORE →' : 'FIND MY FITTING IN CORE →';
      core.hidden = false;
      this.root.setAttribute('aria-busy', 'false');
    } finally {
      clearTimeout(timeout);
    }
  }

  present(payload: EldrickPayload, demo = false): void {
    const launch = importedLaunch(payload);
    this.el('source').textContent = demo
      ? 'SAMPLE FITTING · NOT CUSTOMER DATA'
      : payload.meta.source === 'eldrick-api'
        ? 'YOUR FITTING · FROM ELDRICK API'
        : 'YOUR FITTING · FROM ELDRICK CORE';
    this.el('status').textContent = 'Your swing is the starting point. See what changes across setups.';
    const format = (n: number | null | undefined, digits = 0) => n == null ? 'Est.' : n.toLocaleString('en-US', { maximumFractionDigits: digits });
    this.el('speed').textContent = format(payload.swing.ballSpeed, 1);
    this.el('launch').textContent = format(payload.swing.launchAngle, 1);
    this.el('spin').textContent = format(payload.swing.spinRate);
    const setup = [payload.currentSetup.brand, payload.currentSetup.model].filter(v => typeof v === 'string' && v.trim());
    this.el('setup').textContent = setup.join(' ') || (launch.clubKey === 'driver' ? 'Driver fitting' : 'Iron fitting');
    const source = payload.meta.source === 'eldrick-api' ? 'the ELDRICK API' : 'Core';
    this.el('provenance').textContent = launch.estimated.length
      ? `Estimated inputs: ${launch.estimated.join(', ')}. Other launch inputs are used as supplied by ${source}. The same delivery is retained across comparisons.`
      : `Launch inputs are used as supplied by ${source}. The same delivery is retained across comparisons.`;
    this.el('content').hidden = false;
    this.root.setAttribute('aria-busy', 'false');
    const action = this.el<HTMLButtonElement>('action');
    action.textContent = 'SEE MY FLIGHT →';
    action.disabled = false;
    action.onclick = () => {
      action.disabled = true;
      const app = document.getElementById('mobile-root')!;
      app.inert = false;
      app.style.visibility = '';
      this.root.remove();
      this.onStart(payload);
      document.getElementById('m-cta')?.focus({ preventScroll: true });
    };
    this.el('title').focus({ preventScroll: true });
  }
}
